// External libraries
import { useCallback } from "react";

// FontAwesome icons
import { faFolderTree, faStar, faTag, faUser } from "@fortawesome/pro-duotone-svg-icons";

// Local components
import { CustomTreeView } from "./CustomTreeView";

// Common signals
import {
  allTemplates,
  favoriteDatasets,
  favoriteTemplates,
  userDatasets,
  userEmail,
  userTemplates,
} from "../../Common/Store/CommonSignals";

// Utilities
import { userHasAccessToResource } from "../../Utilities/AuthorizationUtil";

// Local signals
import {
  configuringTreeViewId,
  showTreeViewConfigMenu,
  tileTreeViewConfigs,
} from "./HomepageSignals";

// Get icon based on TreeView type
const getTreeViewIcon = (type) => {
  switch (type) {
    case "favorites":
      return { icon: faStar, primaryColor: "#FFD700", secondaryColor: "#FFA500" };
    case "myTemplates":
      return { icon: faUser, primaryColor: "#4a90d9", secondaryColor: "#2e5a87" };
    case "tagged":
      return { icon: faTag, primaryColor: "#9b59b6", secondaryColor: "#8e44ad" };
    case "custom":
    default:
      return { icon: faFolderTree, primaryColor: "#4a90d9", secondaryColor: "#2e5a87" };
  }
};

// Helper to resolve item from template or dataset map
const resolveItemFromMaps = (id, templateMap, datasetMap) => {
  const template = templateMap.get(id);
  if (template) return { ...template, itemType: "template" };
  const dataset = datasetMap.get(id);
  if (dataset) return { ...dataset, itemType: "dataset" };
  return null;
};

// Build tree items for "favorites" type
const buildFavoritesTreeItems = (itemOrder) => {
  const globalFavoriteTemplateIds = favoriteTemplates.value || [];
  const globalFavoriteDatasetIds = favoriteDatasets.value || [];
  const templateMap = new Map(allTemplates.value.map((t) => [t.id, t]));
  const datasetMap = new Map((userDatasets.value || []).map((d) => [d.id, d]));

  const allFavoriteIds = [
    ...globalFavoriteTemplateIds.map((id) => ({ id, type: "template" })),
    ...globalFavoriteDatasetIds.map((id) => ({ id, type: "dataset" })),
  ];

  let orderedFavoriteIds;
  if (itemOrder && itemOrder.length > 0) {
    const customOrderSet = new Set(itemOrder);
    const newFavorites = allFavoriteIds.filter((f) => !customOrderSet.has(f.id));
    const validCustomOrder = itemOrder
      .map((id) => allFavoriteIds.find((f) => f.id === id))
      .filter(Boolean);
    orderedFavoriteIds = [...validCustomOrder, ...newFavorites];
  } else {
    orderedFavoriteIds = allFavoriteIds;
  }

  return orderedFavoriteIds
    .map((fav) => {
      if (fav.type === "template") {
        const template = templateMap.get(fav.id);
        if (template) return { ...template, itemType: "template" };
      } else if (fav.type === "dataset") {
        const dataset = datasetMap.get(fav.id);
        if (dataset) return { ...dataset, itemType: "dataset" };
      }
      return null;
    })
    .filter(Boolean);
};

// Build tree items for "myTemplates" type
const buildMyTemplatesTreeItems = (email, itemOrder) => {
  const ownerTemplates = (userTemplates.value || []).filter((template) => {
    if (template.owner?.toLowerCase() === email) return true;
    return template.coOwners?.some((co) => {
      if (!co.isUserGroup && co.user?.toLowerCase() === email) return true;
      if (co.isUserGroup && co.userGroupId) {
        return userHasAccessToResource({ coOwners: [co] }, email);
      }
      return false;
    });
  });

  if (itemOrder && itemOrder.length > 0) {
    const customOrderSet = new Set(itemOrder);
    const ownerItemIds = new Set(ownerTemplates.map((t) => t.id));
    const validCustomOrder = itemOrder.filter((id) => ownerItemIds.has(id));
    const newItems = ownerTemplates.filter((t) => !customOrderSet.has(t.id));
    const itemMap = new Map(ownerTemplates.map((t) => [t.id, t]));
    return [...validCustomOrder.map((id) => itemMap.get(id)).filter(Boolean), ...newItems];
  }
  return ownerTemplates;
};

// Build tree items for "tagged" type
const buildTaggedTreeItems = (tagName, itemOrder) => {
  if (!tagName) return [];

  const tagNameLower = tagName.toLowerCase();
  const taggedTemplates = (allTemplates.value || []).filter((template) => {
    const tags = template.properties?.Tags || [];
    return tags.some((tag) => tag?.toLowerCase() === tagNameLower);
  });
  const taggedDatasets = (userDatasets.value || []).filter((dataset) => {
    const tags = dataset.tags || [];
    return tags.some((tag) => tag?.toLowerCase() === tagNameLower);
  });

  const allTaggedItems = [
    ...taggedTemplates.map((t) => ({ ...t, itemType: "template" })),
    ...taggedDatasets.map((d) => ({ ...d, itemType: "dataset" })),
  ];

  if (itemOrder && itemOrder.length > 0) {
    const itemMap = new Map(allTaggedItems.map((item) => [item.id, item]));
    const customOrderSet = new Set(itemOrder);
    const validCustomOrder = itemOrder.filter((id) => itemMap.has(id));
    const newItems = allTaggedItems.filter((item) => !customOrderSet.has(item.id));
    return [...validCustomOrder.map((id) => itemMap.get(id)), ...newItems];
  }

  return allTaggedItems;
};

// Build tree items for "custom" type
const buildCustomTreeItems = (treeItemIds, itemOrder) => {
  const templateMap = new Map(allTemplates.value.map((t) => [t.id, t]));
  const datasetMap = new Map((userDatasets.value || []).map((d) => [d.id, d]));

  if (itemOrder && itemOrder.length > 0) {
    const itemIdSet = new Set(treeItemIds);
    const validOrder = itemOrder.filter((id) => itemIdSet.has(id));
    const newItems = treeItemIds.filter((id) => !itemOrder.includes(id));
    return [...validOrder, ...newItems]
      .map((id) => resolveItemFromMaps(id, templateMap, datasetMap))
      .filter(Boolean);
  }
  return treeItemIds.map((id) => resolveItemFromMaps(id, templateMap, datasetMap)).filter(Boolean);
};

// Build tree data based on TreeView type and config
const buildTreeData = (treeViewConfig, expanded) => {
  const email = userEmail.value?.toLowerCase();
  if (!email) return [];

  const { type, name, treeItemIds = [], itemOrder = [] } = treeViewConfig;
  let treeItems = [];

  switch (type) {
    case "favorites":
      treeItems = buildFavoritesTreeItems(itemOrder);
      break;
    case "myTemplates":
      treeItems = buildMyTemplatesTreeItems(email, itemOrder);
      break;
    case "tagged":
      treeItems = buildTaggedTreeItems(treeViewConfig.tagName, itemOrder);
      break;
    case "custom":
      treeItems = buildCustomTreeItems(treeItemIds, itemOrder);
      break;
    default:
      treeItems = [];
  }

  const items = treeItems.map((item) => {
    // For custom TreeViews, items have itemType property
    if (item.itemType === "dataset") {
      return {
        text: item.datasetName,
        id: item.id,
        itemType: "dataset",
        datasetData: item,
      };
    }
    // For favorites/myTemplates or custom templates
    return {
      text: item.templateName,
      id: item.id,
      itemType: "template",
      templateData: item,
    };
  });

  return [
    {
      text: name,
      expanded: expanded,
      items,
      treeViewConfig,
    },
  ];
};

// Main HomepageTreeView component - renders configured TreeViews for a tile
export const HomepageTreeView = ({ tileId, onTreeViewConfigClick, isLocked }) => {
  // Tile configuration
  const tileConfig = tileTreeViewConfigs.value[tileId];
  const treeViews = tileConfig?.treeViews || [];

  const handleConfigClick = useCallback(
    (treeViewId) => {
      configuringTreeViewId.value = treeViewId;
      showTreeViewConfigMenu.value = true;
      if (onTreeViewConfigClick) {
        onTreeViewConfigClick(tileId, treeViewId);
      }
    },
    [tileId, onTreeViewConfigClick]
  );

  // Placeholder
  if (treeViews.length === 0) {
    return (
      <div className="treeview-placeholder">
        <p>No components in this tile.</p>
        <p className="treeview-placeholder-subtitle">Click the plus icon to add components.</p>
      </div>
    );
  }

  return (
    <div className="treeview-wrapper">
      {treeViews.map((treeViewConfig) => {
        const iconConfig = getTreeViewIcon(treeViewConfig.type);
        const treeData = buildTreeData(treeViewConfig, true);

        return (
          <CustomTreeView
            key={`${tileId}-${treeViewConfig.id}`}
            tileId={tileId}
            treeViewConfig={treeViewConfig}
            onConfigClick={handleConfigClick}
            iconConfig={iconConfig}
            treeData={treeData}
            isLocked={isLocked}
          />
        );
      })}
    </div>
  );
};
